<template>
	<div class="customers-company-details">
		<AppBottomSheet :active="company !== null" :forceWidth="bottomSheetWidth" @toggling-popup="togglingPopup" >
			<div class="company-details-container" v-if="company !== null">
				<ModalHeader
					:title="company?.name+' ('+currencyStore.readableCurrencyValue(company?.balance)+')'"
					:button-text="tabButton"
					:button-disabled="cartButtonDisabled"
					@click-button="tabButtonAction" />
				<AppTabsSwitch :items="[getString('orders'), getString('people'), getString('details')]" @switched="i => tabIndex = i" />
				<div class="tabs-container mt-10">
					<div class="orders-container" v-if="tabIndex === 0">
						<div class="tabs-container">
							<AppTable
								v-if="ordersTabIndex === 0"
								ref="orders-table"
								:fetch-function="fetchFunctionCompanyOrders"
								:table-headings="tableHeadings"
								:filters="filters"
								:title="getString('orders')"
								:total-items="customersStore.companyOrdersTotal"
								:items="ordersToShow"
								initial-sort-field="date">
								<template #settingsIcon="{item, heading}">
									<div class="row centered">
										<div v-if="item.type !== 'paylater'" class="table-text">{{ getString('paid') }}</div>
										<AppTableButtonGroup v-else-if="item.total > 0" :buttons="buttonsAddToCart" :item="item" @picked-add-to-cart="addToCart(item)"/>
										<div v-else class="table-text">N/A</div>
									</div>
								</template>
								<template #before-table>
									<div class="row align-center space-between row-actions mb-10">
										<div class="left-column">
											<AppTabsSwitch auto-switch :items="[getString('orders'), getString('payments')]" :active-item="ordersTabIndex" @switched="i => ordersTabIndex = i" />
										</div>
										<div class="right-column">
											<AppButton small outline :text="getString('pay_all')" @click="payAll" :disabled="!userStore.checkAnyOrderPayAllowed"/>
										</div>
									</div>
								</template>
								<template #order_number="{item}">
									<div class="row centered">
										<p class="payment-intent" :class="{clickable: !!item.order_id}" @click="goToOrder(item.order_id)">{{ item.order_number }}</p>
									</div>
								</template>
							</AppTable>
							<AppTable
								v-if="ordersTabIndex === 1"
								ref="payments-table"
								:fetch-function="fetchFunctionCompanyPayments"
								:table-headings="paymentsTableHeadings"
								:filters="paymentsFilters"
								:title="getString('payments')"
								:total-items="customersStore.companyPaymentsTotal"
								:items="paymentsToShow"
								initial-sort-field="date">
								<template #before-table>
									<div class="row align-center space-between row-actions mb-10">
										<div class="left-column">
											<AppTabsSwitch :items="[getString('orders'), getString('payments')]" :active-item="ordersTabIndex" @switched="i => ordersTabIndex = i" />
										</div>
										<div class="right-column">
										</div>
									</div>
								</template>
								<template #payment-id="{item}">
									<div class="row centered">
										<p class="payment-intent">#{{ item.payment_id }}</p>
									</div>
								</template>
							</AppTable>
						</div>
					</div>
					<div class="people-container" v-else-if="tabIndex === 1">
						<AppTable
							ref="people-table"
							:fetch-function="fetchFunctionCompanyPeople"
							:table-headings="tableHeadingsPeople"
							:filters="filtersPeople"
							:title="getString('people')"
							:total-items="customersStore.companyPeopleTotal"
							:items="peopleToShow" >
							<template #before-table>
								<div class="row align-center space-between row-actions mb-10">
									<div class="left-column">
										<AppButton small outline :text="getString('associate_person')" @click="customersPickerActive = true" />
									</div>
									<div class="right-column">
										<AppButton small outline :text="getString('pay_all')" @click="payAllPeople" :disabled="!userStore.checkAnyOrderPayAllowed"/>
									</div>
								</div>
							</template>
							<template #settingsIcon="{item, heading}">
								<div class="row centered">
									<AppTableButtonGroup v-if="item.balance > 0" :buttons="buttonsPayPerson" :item="item" @picked-pay-person="addToPeopleCart(item)"/>
									<div v-else class="table-text">N/A</div>
								</div>
							</template>
							<template #removeIcon="{item, heading}">
								<div class="row centered">
									<AppTableButtonGroup :buttons="buttonsDelete" :item="item" @picked-delete="removeCustomerAssociation(item)"/>
								</div>
							</template>
						</AppTable>
					</div>
					<div class="details-container mt-10" v-else-if="tabIndex === 2">
						<CustomersCompanyEditDetails @deleted-company="emitDeletedCompany" ref="company-details" :company-id="companyId" />
					</div>
				</div>
			</div>
		</AppBottomSheet>
		<CustomersPicker :active="customersPickerActive" people-only @toggling-popup="togglingPopupPicker" @picked-customer="associateCustomer" />
		<BottomPayment is-registry hide-customer-picker no-pay-later customer-not-editable :items-split="null" :active="paymentActive" @toggling-popup="togglingPayment" @payment-done="paymentDone" @deleted-company="emitDeletedCompany" :total="bottomPaymentAmount" :paid-value="0" :skip-tipping="true" />
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useValuesStore} from "@/shared/stores";
import {useCustomersStore, useCounterStore, useOrdersStore, useUserStore} from "@/console-new/stores";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import {formatDateTime, formatDateYYMMDD, formatDateMMDDHHmm, formatDateOnly} from "@/shared/helpers/dates";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import API from "@/shared/helpers/API";
import AppButton from "@/shared/components/AppButton.vue";
import CustomersPicker from "@/console-new/components/ops/CustomersPicker.vue";
import BottomPayment from "@/console-new/components/ops/counter/BottomPayment.vue";
import CustomersCompanyEditDetails from "@/console-new/components/ops/CustomersCompanyEditDetails.vue";
import CustomersPersonEditDetails from "@/console-new/components/ops/CustomersPersonEditDetails.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		const ordersStore = useOrdersStore();
		const valuesStore = useValuesStore();
		const currencyStore = useCurrencyStore();
		const counterStore = useCounterStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, currencyStore, userStore, customersStore, counterStore, ordersStore, valuesStore, formatDateYYMMDD, formatDateMMDDHHmm, formatDateTime}
	},
	name: "CustomersCompanyDetails",
	components: {
		AppTableButtonGroup,
		CustomersPersonEditDetails,
		CustomersCompanyEditDetails, BottomPayment, CustomersPicker, AppButton, AppTextInput, AppTable, AppButtonIcon, AppTabsSwitch, AppRadioButtonGroup, AppGoogleMapsInput, AppSelect, ModalHeader, AppBottomSheet},
	mixins: [WindowWidth],
	emits: ['toggling-popup', 'deleted-company'],
	props: {
		companyId: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			cart: [],
			cartPeople: [],
			cartPeopleRealTotal: 0,
			cartPeopleOrderIds: [],
			paymentActive: false,
			customersPickerActive: false,
			toUpdate: false,
			tabIndex: 0,
			ordersTabIndex: 0,
			filters: [
				{
					type: "price",
					key: "total",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('order_number'),
					cols: 1,
					sortable: false,
					fieldName: 'order_number',
					slotName: 'order_number',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString('total'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_total',
				},
				{
					text: this.getString('payment'),
					cols: 1,
					fieldName: 'settings',
					slotName: 'settingsIcon',
				},
			],
			paymentsFilters: [
				{
					type: "price",
					key: "amount",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			paymentsTableHeadings: [
				{
					text: this.getString('payment_id'),
					cols: 1,
					sortable: false,
					fieldName: 'payment_id',
					slotName: 'payment-id',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					active: true,
					sortAsc: false,
					fieldName: 'date',
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_total',
				}
			],
			filtersPeople: [
				{
					type: "price",
					key: "balance",
					text: this.getString("balance"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			tableHeadingsPeople: [
				{
					text: this.getString('name'),
					cols: 2,
					sortable: true,
					fieldName: 'name',
				},
				{
					text: this.getString('balance'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_balance',
				},
				{
					text: this.getString('payment'),
					cols: 1,
					fieldName: 'settings',
					slotName: 'settingsIcon',
				},
				{
					text: this.getString('remove'),
					cols: 1,
					fieldName: 'settings',
					slotName: 'removeIcon',
				},
			],
		}
	},
	computed: {
		company() {
			return this.companyId ? this.customersStore.customersCompanies.find(company => company.customer_id === this.companyId) : null;
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		tabButton() {
			if(this.tabIndex === 0) {
				if(this.cartTotal > 0) {
					return this.currencyStore.readableCurrencyValue(this.cartTotal);
				} else {
					return this.getString('pay');
				}
			} else if(this.tabIndex === 1) {
				if(this.cartPeopleTotal > 0) {
					return this.currencyStore.readableCurrencyValue(this.cartPeopleTotal);
				} else {
					return this.getString('pay');
				}
			} else if(this.tabIndex === 2) {
				return this.getString('update');
			} else {
				return '';
			}
		},
		paymentsToShow() {
			return this.customersStore.companyPayments.map(payment => {
				return {
					...payment,
					readable_total: this.currencyStore.readableCurrencyValue(payment.amount),
					date: this.formatDateMMDDHHmm(payment.date),
				}
			})
		},
		ordersToShow() {
			return this.customersStore.companyOrders.map(order => {
				return {
					...order,
					readable_total: this.currencyStore.readableCurrencyValue(order.total),
					date: this.formatDateMMDDHHmm(order.date),
				}
			})
		},
		peopleToShow() {
			return this.customersStore.companyPeople.map(person => {
				return {
					...person,
					readable_balance: this.currencyStore.readableCurrencyValue(person.balance),
				}
			})
		},
		cartTotal() {
			return this.cart.reduce((acc, curr) => acc + curr.total, 0);
		},
		cartButtonDisabled() {
			if(!this.userStore.checkAnyOrderPayAllowed){
				return true;
			}

			if(this.tabIndex === 0) {
				return this.cartTotal <= 0;
			} else if(this.tabIndex === 1) {
				return this.cartPeopleTotal <= 0;
			} else {
				return false;
			}
		},
		cartPeopleTotal() {
			return this.cartPeople.reduce((acc, curr) => acc + curr.total, 0);
		},
		bottomPaymentAmount() {
			if(this.tabIndex === 0) {
				return this.cartTotal;
			} else if(this.tabIndex === 1) {
				return this.cartPeopleRealTotal;
			} else {
				return 0;
			}
		},
		buttonsDelete() {
			return [
				{
					iconName: "icon-trash-blue",
					disabled: !this.userStore.checkAnyOrderPayAllowed,
					class: "outline",
					emitName: "picked-delete",
					title: this.getString('delete'),
					active: false
				}
			];
		},
		buttonsPayPerson() {
			return [
				{
					disabled: !this.userStore.checkAnyOrderPayAllowed,
					emitName: "picked-pay-person",
					title: this.getString('pay'),
					activeFunction: (item) => this.isPersonInCart(item),
				},
			]
		},
		buttonsAddToCart() {
			return [
				{
					disabled: !this.userStore.checkAnyOrderPayAllowed,
					emitName: "picked-add-to-cart",
					title: this.getString('pay'),
					activeFunction: (item) => this.isOrderInCart(item),
				},
			]
		},
	},
	methods: {
		togglingPopup(open) {
			this.$emit('toggling-popup', open);
			this.ordersTabIndex = 0;
		},
		goToOrder(orderId) {
			if(orderId) {
				this.ordersStore.search = "id:"+orderId;
				this.ordersStore.showCompleted = true;
				this.ordersStore.filterCounter = true;
				this.ordersStore.filterDelivery = true;
				this.ordersStore.filterTakeaway = true;
				this.ordersStore.filterRooms = true;
				this.ordersStore.refreshOrders();
				this.$router.push("/ops/orders");

				this.valuesStore.forceCloseAllBottomSheets = true;
				setTimeout(() => {
					this.valuesStore.forceCloseAllBottomSheets = false;
				}, 1000);
			}
		},
		isOrderInCart(order) {
			return !!(this.cart.find(o => o.order_id === order.order_id));
		},
		isPersonInCart(person) {
			return !!(this.cartPeople.find(o => o.customer_id === person.customer_id));
		},
		addToCart(order) {
			let existing = this.cart.find(o => o.order_id === order.order_id);
			if(existing) {
				this.cart.splice(this.cart.indexOf(existing), 1);
			} else {
				this.cart.push({
					order_id: order.order_id,
					total: order.total,
				});
			}
		},
		addToPeopleCart(person) {
			let existing = this.cartPeople.find(o => o.customer_id === person.customer_id);
			if(existing) {
				this.cartPeople.splice(this.cartPeople.indexOf(existing), 1);
			} else {
				this.cartPeople.push({
					customer_id: person.customer_id,
					total: person.balance,
				});
			}
		},
		togglingPopupPicker(open) {
			if(!open) {
				this.customersPickerActive = false;
			}
		},
		togglingPayment(open) {
            if(!open) {
                this.paymentActive = false;
            }
        },
		paymentDone(type, value, partialType, orderAssociatedId, itemsSplit, dutchPeople, discountOnTotal, discountPartial, appliedCoupons, printSingleItem, callback) {
			let invoiceData = {
				invoice_paid: true,
			};
			if(!!type && ["wire", "check"].includes(type)) {
				invoiceData.invoice_paid = this.counterStore.setInvoiceAsPaid;
			}
			if(this.counterStore.invoiceAdditionalData?.purchase_order) {
				invoiceData.purchase_order = this.counterStore.invoiceAdditionalData.purchase_order;
			}
			if(!invoiceData.invoice_paid && this.counterStore.invoiceAdditionalData?.payment_due_date) {
				invoiceData.payment_due_date = formatDateOnly(this.counterStore.invoiceAdditionalData.payment_due_date);
			}
			if(this.tabIndex === 0) {
				const total = this.cartTotal;
				API.init().payCustomersOrders(this.userStore.currBusinessId, this.cart.map(o => o.order_id), type, JSON.stringify(invoiceData), total, this.company.customer_id, "company", discountOnTotal, (response) => {
					this.paymentActive = false;
					callback && callback();
					if(response && response.result && response.result === 'OK') {
						this.showNotification("payment-success");
						this.cart = [];
						this.$emit('toggling-popup', false);
					} else {
						this.showNotification("generic-fail");
					}
				});
			} else if(this.tabIndex === 1) {
				API.init().payCompanyAssociatesOrders(this.userStore.currBusinessId, this.cartPeopleOrderIds, type, JSON.stringify(invoiceData), this.cartPeopleRealTotal, this.company.customer_id, (response) => {
					this.paymentActive = false;
					callback && callback();
					if(response && response.result && response.result === 'OK') {
						this.showNotification("payment-success");
						this.cartPeople = [];
						this.cartPeopleRealTotal = 0;
						this.cartPeopleOrderIds = [];
						this.$emit('toggling-popup', false);
					} else {
						this.showNotification("generic-fail");
					}
				});
			}

		},
		associateCustomer(customer) {

			API.init().associateCustomerToCompany(this.userStore.currBusinessId, this.company.customer_id, customer.customer_id, (response) => {
				if(response && response.result && response.result === "OK") {
					this.showNotification("generic-success");
					this.$refs && this.$refs['people-table'] && this.$refs['people-table'].fetchData();
					this.customersPickerActive = false;
				} else {
					if(response && response.details && response.details === "already_associated") {
						this.showNotification("associated-already");
					} else {
						this.showNotification("generic-fail");
					}
				}
			});
		},
		removeCustomerAssociation(person) {
			API.init().removeCustomerCompanyPerson(this.userStore.currBusinessId, this.company.customer_id, person.customer_id, (response) => {
				if(response && response.result && response.result === "OK") {
					this.showNotification("generic-success");
					this.$refs && this.$refs['people-table'] && this.$refs['people-table'].fetchData();
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
		fetchFunctionCompanyOrders(filters, callback) {
			if(filters.sort && filters.sort === 'readable_total') {
				filters.sort = 'total';
			}
			return this.customersStore.getFilteredCompanyOrders(this.company.customer_id, filters, callback);
		},
		fetchFunctionCompanyPayments(filters, callback) {
			if(filters.sort && filters.sort === 'readable_total') {
				filters.sort = 'amount';
			}
			return this.customersStore.getFilteredCompanyPayments(this.company.customer_id, filters, callback);
		},
		fetchFunctionCompanyPeople(filters, callback) {
			if(filters.sort && filters.sort === 'readable_balance') {
				filters.sort = 'balance';
			}
			return this.customersStore.getFilteredCompanyPeople(this.company.customer_id, filters, callback);
		},
		tabButtonAction() {
			if(this.tabIndex === 0) {
				if(this.cartTotal > 0) {
					this.paymentActive = true;
				}
			} else if(this.tabIndex === 1) {
				if(this.cartPeopleTotal > 0) {

					API.init().getCustomersOrdersTotal(this.userStore.currBusinessId, this.company.customer_id, this.cartPeople.map(o => o.customer_id), (response) => {
						if(response && response.result && response.result === 'OK' && response.total) {
							this.cartPeopleRealTotal = response.total;
							this.cartPeopleOrderIds = response.orders;
							this.paymentActive = true;
						} else {
							this.showNotification("generic-fail");
						}
					});
				}
			} else if(this.tabIndex === 2) {
				this.$refs['company-details'] && this.$refs['company-details'].updateCompany();
			}
		},
		payAll() {

			API.init().getCustomersAllOrders(this.userStore.currBusinessId, this.company.customer_id, "company", (response) => {
				if(response && response.result && response.result === 'OK' && response.orders) {
					if(response.orders.length === 0) {
						this.showNotification("nothing-to-pay");
						return;
					}
					this.cart = response.orders.map(o => {
						return {
							order_id: Number(o.order_id),
							total: o.total ? Number(o.total) : 0,
						}
					});
					this.paymentActive = true;
				} else {
					this.showNotification("generic-fail");
				}
			});

		},
		payAllPeople() {

			API.init().getCustomersOrdersTotal(this.userStore.currBusinessId, this.company.customer_id, null, (response) => {
				if(response && response.result && response.result === 'OK') {
					if(!response.total || response.total <= 0) {
						this.showNotification("nothing-to-pay");
						return;
					}
					this.cartPeopleRealTotal = response.total;
					this.cartPeopleOrderIds = response.orders;
					this.paymentActive = true;
					this.cartPeople = [];
				} else if(response && response.details && response.details === "no_customers_found") {
					this.showNotification("nothing-to-pay");
				} else {
					this.showNotification("generic-fail");
				}
			});

		},
		emitDeletedCompany(c) {
			this.$emit('deleted-company', c);
		}
	},
	watch: {
		companyId(newValue, oldValue) {
			if(newValue !== oldValue) {
				if(newValue) {
					this.tabIndex = 0;
					this.customersPickerActive = false;
					this.paymentActive = false;
					this.toUpdate = false;
					this.cart = [];
				}
			}
		},
	}
}
</script>

<style scoped lang="scss">
:deep(.table-wrapper) {
	padding-top: 0;
	margin-top: 10px;
	.row-actions {
		padding: 10px 10px 0;
	}
}
.orders-container, .people-container {
	.card-container {
		box-shadow: none;
		&:deep(.card-header) {
			display: none;
		}
	}
}
.table-text {
	font-size: 12px;
}
.row-actions {
	gap: 8px;
}

.payment-intent {
	text-align: left;
	width: 100%;
	font-size: 12px;

	&.clickable {
		cursor: pointer;
		color: var(--color-primary);
		text-decoration: underline;
	}
}
</style>